import { HomeOutlined } from '@mui/icons-material';
import { List, ListItem, ListItemButton, ListItemIcon } from '@mui/material';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import integrityLogo from '../../assets/dmd/integrity-navy.svg';
import { copyrightText, LEGAL_LINKS } from '../alliance/alliance-apps';
import { DMD_BRAND } from '../alliance/alliance-tokens';
import { SIDEBAR_COLORS } from '../alliance/app-nav-drawer';
import { SelectAgentImpersonate } from '../select-agent-impersonate';
import { NavDataLink, NavDataSection } from './nav-data';
import { navRowSx } from './nav-row';
import { NavSection } from './nav-section';

/** True when a link, or one of its children, routes to `pathname` */
const linksTo = (params: { link: NavDataLink; pathname: string }): boolean =>
  params.link.to === params.pathname ||
  !!params.link.children?.some((child) =>
    linksTo({ link: child, pathname: params.pathname }),
  );

/**
 * The sidebar body under the navy header (AppNavDrawer): the Impersonation
 * Search band, Home and the nav sections (one open at a time; the one
 * holding the current page opens when you navigate to it), then the
 * Integrity logo, the legal links and the copyright.
 *
 * Only the nav scrolls. The footer stays at the bottom of the sidebar and
 * keeps 76px clear for the CookieYes consent button, which is fixed to the
 * window's bottom-left corner, over the sidebar.
 */
export function NavDrawerBody(props: { data: NavDataSection[] }) {
  // State
  const [open, setOpen] = React.useState<string | null>(null);
  // Hooks
  const location = useLocation();

  const handleOpen = (title: string) => {
    title === open ? setOpen(null) : setOpen(title);
  };

  const isHome = location.pathname === '/home';

  React.useEffect(() => {
    // Open the section that holds the current page, so its row shows
    const current = props.data.find((section) =>
      section.links.some((link) =>
        linksTo({ link, pathname: location.pathname }),
      ),
    );
    if (current) {
      setOpen(current.title);
    }
  }, [location.pathname, props.data]);

  return (
    <React.Fragment>
      <Box
        sx={{
          p: '6px',
          backgroundColor: SIDEBAR_COLORS.strip,
          borderBottom: '1px solid #0000000f',
          position: 'relative',
          zIndex: 5,
        }}
      >
        <SelectAgentImpersonate />
      </Box>

      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <List
          component="div"
          aria-label="Dashboard"
          sx={{
            flex: 1,
            p: '8px 8px 8px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/home"
              aria-current={isHome ? 'page' : undefined}
              sx={navRowSx({ active: isHome, depth: 0 })}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HomeOutlined color="primary" />
              </ListItemIcon>
              <ListItemText primary="Home" disableTypography />
            </ListItemButton>
          </ListItem>

          {props.data.map((item) => (
            <NavSection
              key={item.title}
              item={item}
              open={open === item.title}
              handleOpen={() => handleOpen(item.title)}
            />
          ))}
        </List>
      </Box>

      <Box
        component="footer"
        sx={{
          p: '14px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          borderTop: '1px solid #0000000f',
          flex: 'none',
        }}
      >
        <Box
          component="img"
          src={integrityLogo}
          alt="Integrity"
          sx={{ display: 'block', height: 18, width: 126, mb: '6px' }}
        />
        <Box sx={{ display: 'flex', gap: 2, fontSize: 12, fontWeight: 500 }}>
          {LEGAL_LINKS.map((link) => (
            <Box
              key={link.label}
              component="a"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: DMD_BRAND.navy,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {link.label}
            </Box>
          ))}
        </Box>
        <Box sx={{ fontSize: 12, color: '#00000099' }}>{copyrightText()}</Box>
      </Box>
    </React.Fragment>
  );
}
